import { useEffect, useState, type RefObject } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@fq/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@fq/ui/components/ui/dialog';
import { Label } from '@fq/ui/components/ui/label';
import { Switch } from '@fq/ui/components/ui/switch';
import { Textarea } from '@fq/ui/components/ui/textarea';
import {
  buildPickemShareUrl,
  buildPickemTweetText,
  buildPickemTweetUrl,
  eventHashtag,
  shortCodeFromImageKey,
} from './share-pickem';
import type { PickemTweetContext } from './share-pickem.types';
import {
  captureBracketImage,
  uploadPickemShareImage,
} from './capture-bracket';

export type ShareImageCaptureDeps = {
  /** DOM ref to the bracket root to snapshot. */
  bracketRef: RefObject<HTMLElement | null>;
  /** Event id used for both the upload key and the share URL. */
  eventId: string;
  /** Returns a Bearer token, or null if signed out. */
  getAuthToken: () => string | null;
  /** Absolute API base URL — must be reachable from the public internet
   * (Twitter's crawler hits the unfurl endpoint). */
  apiBaseUrl: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: PickemTweetContext;
  /** Fallback URL shown when image capture is disabled or unavailable. */
  eventPageUrl?: string;
  /** Caller-provided "open this URL in a browser" — platform-specific
   * (window.open on web, overwolf.utils.openUrlInDefaultBrowser in OW). */
  openExternalUrl: (url: string) => void;
  /** When provided, enables bracket image capture + upload. Caller must
   * supply the bracket DOM ref and authn/api context. */
  imageCapture?: ShareImageCaptureDeps;
};

const TWEET_LIMIT = 280;

export function SharePickemDialog({
  open,
  onOpenChange,
  context,
  eventPageUrl,
  openExternalUrl,
  imageCapture,
}: Props) {
  const canIncludeImage = !!imageCapture && !!imageCapture.getAuthToken();
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [includeImage, setIncludeImage] = useState(canIncludeImage);
  const [isPreparing, setIsPreparing] = useState(false);

  useEffect(() => {
    if (open) {
      setText(buildPickemTweetText(context));
      setUrl(context.url ?? eventPageUrl ?? '');
      setIncludeImage(canIncludeImage);
      setIsPreparing(false);
    }
  }, [open, context, eventPageUrl, canIncludeImage]);

  const remaining = TWEET_LIMIT - text.length - (url ? url.length + 1 : 0);
  const overLimit = remaining < 0;

  const handleOpenTwitter = async () => {
    let finalUrl = url || eventPageUrl || '';

    if (includeImage && imageCapture && imageCapture.bracketRef.current) {
      setIsPreparing(true);
      try {
        const blob = await captureBracketImage(imageCapture.bracketRef.current, {
          eventName: context.eventName,
          hashtag: eventHashtag(context.eventName),
        });
        const { imageKey } = await uploadPickemShareImage(
          imageCapture.eventId,
          blob,
          {
            apiBaseUrl: imageCapture.apiBaseUrl,
            getAuthToken: imageCapture.getAuthToken,
          },
        );
        finalUrl = buildPickemShareUrl({
          apiBaseUrl: imageCapture.apiBaseUrl,
          eventId: imageCapture.eventId,
          imageShortCode: shortCodeFromImageKey(imageKey),
        });
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : 'Failed to prepare bracket image',
        );
        setIsPreparing(false);
        return;
      }
      setIsPreparing(false);
    }

    const tweetUrl = buildPickemTweetUrl(text, finalUrl || undefined);
    openExternalUrl(tweetUrl);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isPreparing) onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share on X / Twitter</DialogTitle>
          <DialogDescription>
            {canIncludeImage
              ? 'Edit your tweet. We’ll attach an image of the bracket so it shows up in the post.'
              : 'Edit the tweet, then open X in a new tab.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="tweet-text">Tweet</Label>
            <Textarea
              id="tweet-text"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isPreparing}
            />
            <p
              className={`text-xs ${
                overLimit ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              {remaining} characters remaining
            </p>
          </div>
          {canIncludeImage && (
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <Label
                  htmlFor="include-bracket-image"
                  className="text-sm font-medium"
                >
                  Include bracket image
                </Label>
                <p className="text-xs text-muted-foreground">
                  Adds a snapshot of the current bracket to the tweet card.
                </p>
              </div>
              <Switch
                id="include-bracket-image"
                checked={includeImage}
                onCheckedChange={setIncludeImage}
                disabled={isPreparing}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="lg"
            className="sm:h-9 sm:px-4"
            onClick={() => onOpenChange(false)}
            disabled={isPreparing}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="sm:h-9 sm:px-4"
            onClick={handleOpenTwitter}
            disabled={!text.trim() || overLimit || isPreparing}
          >
            {isPreparing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4 mr-2" />
            )}
            {isPreparing ? 'Preparing image…' : 'Open in X'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
